import * as React from "react"

import { cn } from "@shared/lib/utils"

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

const NumericShim = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onChange, value, defaultValue, min, max, ...rest }, ref) => {
    // Format a numeric value into a concise string without floating artifacts
    const formatFromValue = React.useCallback((v: any): string => {
      if (v === null || v === undefined) return "";
      if (typeof v === "string") return v;
      if (typeof v !== "number" || Number.isNaN(v)) return "";
      // 15 significant digits to avoid artifacts, then normalize via parseFloat -> toString
      const str = Number(v).toPrecision(15);
      const normalized = parseFloat(str).toString();
      return normalized;
    }, []);

    const [display, setDisplay] = React.useState<string>(formatFromValue(value ?? defaultValue));
    const [isFocused, setIsFocused] = React.useState<boolean>(false);

    // Keep display in sync when not focused
    React.useEffect(() => {
      if (!isFocused) {
        setDisplay(formatFromValue(value));
      }
    }, [value, isFocused, formatFromValue]);

    const allowNegative = (() => {
      if (typeof min === 'number') return min < 0;
      return false;
    })();

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      const raw = e.target.value;
      const allowed = allowNegative ? /^-?\d*(?:\.)?\d*$/ : /^\d*(?:\.)?\d*$/;
      if (!allowed.test(raw)) {
        return; // reject invalid characters
      }
      setDisplay(raw);
      onChange?.(e);
    };

    return (
      <input
        {...rest}
        ref={ref}
        type="text"
        inputMode="decimal"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        value={display}
        onChange={handleChange}
        onFocus={(e) => { setIsFocused(true); rest.onFocus?.(e); }}
        onBlur={(e) => { setIsFocused(false); rest.onBlur?.(e); }}
      />
    )
  }
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    if (type === 'number') {
      return <NumericShim className={className} type={type} ref={ref} {...props} />
    }
    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
